import React, { useState, useEffect } from 'react';
import {
  FileText,
  Send,
  Check,
  CheckCircle,
  XCircle,
  Copy,
  Shield,
  BookOpen,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import { messageApi, signingApi } from '../../services/iso20022Api';
import { copyToClipboard, downloadFile, validateJson } from '../../utils/helpers';
import {
  MESSAGE_SAMPLES,
  DATE_TIME_FIELDS,
  createFreshSample,
  withFreshIds
} from '../../constants/messageSamples';

// The engine stores every message it signs and refuses the same payload twice.
// Turn that database error into something a participant can act on.
const describeFailure = (err, keepIds) => {
  const msg = err.response?.data?.message || err.response?.data?.error || err.message || '';
  if (/Duplicate entry/i.test(msg)) {
    return keepIds
      ? 'This message was already signed with these ids. Untick “Keep these ids” to send fresh ones.'
      : 'This message was already signed with these ids. Generate it again to get fresh ones.';
  }
  return msg;
};

// Message-family groups shown as <optgroup> labels, in display order.
const FAMILY_ORDER = ['pacs', 'pain', 'acmt', 'camt', 'admi'];
const FAMILY_LABELS = {
  pacs: 'Payments (pacs)',
  pain: 'Payment Initiation (pain)',
  acmt: 'Account Management (acmt)',
  camt: 'Cash Management (camt)',
  admi: 'Administration (admi)',
};

/** Groups schemas by family prefix, families in FAMILY_ORDER, types sorted within each. */
const groupSchemas = (schemas) => {
  const byFamily = new Map();
  for (const s of schemas) {
    const family = s.shortType.split('.')[0];
    if (!byFamily.has(family)) byFamily.set(family, []);
    byFamily.get(family).push(s);
  }
  const order = (f) => {
    const i = FAMILY_ORDER.indexOf(f);
    return i === -1 ? FAMILY_ORDER.length : i;
  };
  return [...byFamily.entries()]
    .sort((a, b) => order(a[0]) - order(b[0]) || a[0].localeCompare(b[0]))
    .map(([family, list]) => [
      family,
      list.sort((a, b) => a.shortType.localeCompare(b.shortType)),
    ]);
};

const MessageGenerator = () => {
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState('');
  const [inputJson, setInputJson] = useState('');
  const [outputFormat, setOutputFormat] = useState('xml');
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [signedMessage, setSignedMessage] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [frontendValidation, setFrontendValidation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('unsigned'); // 'unsigned' or 'signed'
  const [showSampleModal, setShowSampleModal] = useState(false);
  const [copied, setCopied] = useState(false);
  // Off by default: every Generate stamps new ids and a new creation time, as a
  // gateway would. Tick it only to resend the same message on purpose (a replay test).
  const [keepIds, setKeepIds] = useState(false);

  // The body to send: parsed from the editor, with fresh ids unless kept. The
  // editor is updated so it shows exactly what was generated.
  const prepareInput = () => {
    const parsed = validateJson(inputJson);
    if (!parsed.isValid) return parsed;
    if (keepIds) return parsed;
    const data = withFreshIds(parsed.data);
    setInputJson(JSON.stringify(data, null, 2));
    return { ...parsed, data };
  };

  useEffect(() => {
    loadSchemas();
  }, []);

  const loadSchemas = async () => {
    try {
      const data = await messageApi.listSchemas();
      const list = data.schemas || [];
      setSchemas(list);
      if (list.length > 0) {
        // Default to pacs.008 (the corridor's primary flow), falling back to
        // the first generatable type in display order.
        const grouped = groupSchemas(list).flatMap(([, s]) => s);
        const first =
          grouped.find((s) => s.shortType === 'pacs.008' && s.generatorAvailable !== false) ||
          grouped.find((s) => s.generatorAvailable !== false) ||
          grouped[0];
        setSelectedSchema(first.shortType);
        loadSampleJson(first.shortType);
      }
    } catch (err) {
      console.error('Failed to load schemas:', err);
    }
  };

  const loadSampleJson = (messageType) => {
    // Use createFreshSample to get a sample with updated timestamps and IDs
    const freshSample = createFreshSample(messageType);
    if (freshSample) {
      setInputJson(JSON.stringify(freshSample, null, 2));
    } else {
      setInputJson('{}');
    }
  };

  const handleSchemaChange = (schemaType) => {
    setSelectedSchema(schemaType);
    loadSampleJson(schemaType);
    setGeneratedMessage('');
    setSignedMessage('');
    setValidationResult(null);
    setFrontendValidation(null);
    setError(null);
    setActiveTab('unsigned');
  };

  const handleLoadSample = () => {
    loadSampleJson(selectedSchema);
    setShowSampleModal(false);
    setFrontendValidation(null);
    setValidationResult(null);
    setError(null);
  };

  // Frontend validation - checks structure without backend call
  const validateInputFrontend = () => {
    setFrontendValidation(null);
    setError(null);

    // Check JSON syntax
    const jsonValidation = validateJson(inputJson);
    if (!jsonValidation.isValid) {
      setFrontendValidation({
        valid: false,
        errors: [{ field: 'JSON Syntax', message: jsonValidation.error }]
      });
      return false;
    }

    const data = jsonValidation.data;
    const sampleData = MESSAGE_SAMPLES[selectedSchema];

    if (!sampleData) {
      setFrontendValidation({
        valid: true,
        message: 'No validation rules defined for this message type'
      });
      return true;
    }

    const errors = [];
    const rules = sampleData.validationRules;

    // Check required top-level fields
    if (rules.required) {
      rules.required.forEach(field => {
        if (!data[field]) {
          errors.push({
            field: field,
            message: `Required field missing`
          });
        }
      });
    }

    // Check nested required fields
    Object.keys(rules).forEach(key => {
      if (key !== 'required' && data[key]) {
        const nestedRules = rules[key];
        if (Array.isArray(nestedRules)) {
          nestedRules.forEach(nestedField => {
            if (!data[key][nestedField]) {
              errors.push({
                field: `${key}.${nestedField}`,
                message: `Required nested field missing`
              });
            }
          });
        }
      }
    });

    // Validate array fields
    if (selectedSchema === 'pacs.008' && data.cdtTrfTxInf) {
      if (!Array.isArray(data.cdtTrfTxInf)) {
        errors.push({
          field: 'cdtTrfTxInf',
          message: 'Must be an array'
        });
      } else if (data.cdtTrfTxInf.length === 0) {
        errors.push({
          field: 'cdtTrfTxInf',
          message: 'Must contain at least one transaction'
        });
      }
    }

    if (selectedSchema === 'pacs.002' && data.txInfAndSts) {
      if (!Array.isArray(data.txInfAndSts)) {
        errors.push({
          field: 'txInfAndSts',
          message: 'Must be an array'
        });
      }
    }

    // Validate date formats
    const checkDateFormats = (obj, prefix = '') => {
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        const fullKey = prefix ? `${prefix}.${key}` : key;

        if (DATE_TIME_FIELDS.includes(key) && typeof value === 'string') {
          // Check ISO 8601 format
          if (!value.match(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:\d{2})?)?$/)) {
            errors.push({
              field: fullKey,
              message: 'Invalid date format (expected ISO 8601: YYYY-MM-DD or YYYY-MM-DDTHH:MM:SSZ)'
            });
          }
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          checkDateFormats(value, fullKey);
        }
      });
    };
    checkDateFormats(data);

    // Validate currency codes (if applicable)
    const checkCurrencyCodes = (obj, prefix = '') => {
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        const fullKey = prefix ? `${prefix}.${key}` : key;

        if (key === 'ccy' && typeof value === 'string') {
          if (!value.match(/^[A-Z]{3}$/)) {
            errors.push({
              field: fullKey,
              message: 'Invalid currency code (expected 3-letter ISO 4217 code)'
            });
          }
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          checkCurrencyCodes(value, fullKey);
        } else if (Array.isArray(value)) {
          value.forEach((item, idx) => {
            if (typeof item === 'object' && item !== null) {
              checkCurrencyCodes(item, `${fullKey}[${idx}]`);
            }
          });
        }
      });
    };
    checkCurrencyCodes(data);

    const validationResult = {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
      message: errors.length === 0 ? 'Input structure is valid' : undefined
    };

    setFrontendValidation(validationResult);
    return validationResult.valid;
  };

  // Backend validation - comprehensive validation via API
  const validateInput = async () => {
    setValidationResult(null);
    setFrontendValidation(null);
    setError(null);

    // First run frontend validation
    if (!validateInputFrontend()) {
      return false;
    }

    // Check JSON syntax again for backend
    const jsonValidation = validateJson(inputJson);
    if (!jsonValidation.isValid) {
      setError('Invalid JSON: ' + jsonValidation.error);
      return false;
    }

    try {
      setLoading(true);
      const result = await messageApi.validateMessage(selectedSchema, jsonValidation.data);
      setValidationResult(result);
      return result.valid;
    } catch (err) {
      setError('Backend validation failed: ' + (err.response?.data?.message || err.message));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const generateMessage = async () => {
    setError(null);
    setGeneratedMessage('');
    setSignedMessage('');
    setActiveTab('unsigned');

    // Validate JSON first
    const jsonValidation = prepareInput();
    if (!jsonValidation.isValid) {
      setError('Invalid JSON: ' + jsonValidation.error);
      return;
    }

    try {
      setLoading(true);
      // Always use pretty=true for better readability
      const message = await messageApi.generateMessage(
        selectedSchema,
        jsonValidation.data,
        outputFormat,
        true // pretty=true
      );
      // JSON output arrives parsed into an object, which React cannot render
      // (it blanked the whole portal); show it as text.
      setGeneratedMessage(typeof message === 'string' ? message : JSON.stringify(message, null, 2));
    } catch (err) {
      setError('Generation failed: ' + describeFailure(err, keepIds));
    } finally {
      setLoading(false);
    }
  };

  const generateAndSign = async () => {
    setError(null);
    setGeneratedMessage('');
    setSignedMessage('');

    // Validate JSON first
    const jsonValidation = prepareInput();
    if (!jsonValidation.isValid) {
      setError('Invalid JSON: ' + jsonValidation.error);
      return;
    }

    try {
      setLoading(true);
      setSigning(true);

      // Step 1: Generate the XML message with pretty=true
      const unsignedXml = await messageApi.generateMessage(
        selectedSchema,
        jsonValidation.data,
        'xml', // Always generate XML for signing
        true // pretty=true for better readability
      );

      setGeneratedMessage(unsignedXml);

      // Step 2: Sign the generated XML with pretty=true
      const signed = await signingApi.signMessage(unsignedXml, true);
      setSignedMessage(signed);
      setActiveTab('signed'); // Switch to signed tab

    } catch (err) {
      setError('Generation/Signing failed: ' + describeFailure(err, keepIds));
    } finally {
      setLoading(false);
      setSigning(false);
    }
  };

  const handleCopy = async (content) => {
    const ok = await copyToClipboard(content || generatedMessage);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = (content, isSigned = false) => {
    const messageToDownload = content || generatedMessage;
    // Signed output is always XML, whatever the format selector says.
    const isXml = isSigned || /^\s*</.test(messageToDownload || '');
    const extension = isXml ? 'xml' : 'json';
    const mimeType = isXml ? 'application/xml' : 'application/json';
    const suffix = isSigned ? '-signed' : '';

    downloadFile(
      messageToDownload,
      `${selectedSchema}${suffix}-${Date.now()}.${extension}`,
      mimeType
    );
  };

  const currentSample = MESSAGE_SAMPLES[selectedSchema];
  const groupedSchemas = groupSchemas(schemas);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Message Generator</h1>
        <p className="text-gray-600 mt-1">Generate and sign ISO 20022 messages from JSON input</p>
      </div>

      {/* Configuration */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Configuration</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Type
              </label>
              <select
                className="select"
                value={selectedSchema}
                onChange={(e) => handleSchemaChange(e.target.value)}
              >
                {groupedSchemas.map(([family, familySchemas]) => (
                  <optgroup key={family} label={FAMILY_LABELS[family] || family}>
                    {familySchemas.map((schema) => (
                      <option
                        key={schema.shortType}
                        value={schema.shortType}
                        disabled={schema.generatorAvailable === false}
                      >
                        {schema.shortType} - {schema.name}
                        {schema.generatorAvailable === false ? ' (schema only)' : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {currentSample && (
                <p className="mt-1 text-xs text-gray-500">{currentSample.description}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Output Format
              </label>
              <select
                className="select"
                value={outputFormat}
                onChange={(e) => setOutputFormat(e.target.value)}
                disabled={signing}
              >
                <option value="xml">XML</option>
                <option value="json">JSON</option>
              </select>
              {signing && (
                <p className="text-xs text-gray-500 mt-1">XML required for signing</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Input/Output Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Input JSON</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleLoadSample}
                className="btn-secondary text-sm flex items-center space-x-2"
                title="Load sample JSON for this message type"
              >
                <Sparkles className="w-4 h-4" />
                <span>Sample</span>
              </button>
              <button
                onClick={validateInputFrontend}
                disabled={loading}
                className="btn-secondary text-sm flex items-center space-x-2"
                title="Quick validation check"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Validate</span>
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            <textarea
              className="w-full h-96 p-4 font-mono text-sm border-0 focus:ring-0 resize-none"
              value={inputJson}
              onChange={(e) => {
                setInputJson(e.target.value);
                setFrontendValidation(null);
                setValidationResult(null);
                setError(null);
              }}
              placeholder="Enter your JSON payload here..."
            />
          </div>
          
          {/* Validation Feedback */}
          {frontendValidation && (
            <div className={`px-6 py-3 border-t ${
              frontendValidation.valid ? 'bg-success-50 border-success-200' : 'bg-danger-50 border-danger-200'
            }`}>
              {frontendValidation.valid ? (
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-success-900">Structure Valid</p>
                    <p className="text-xs text-success-700 mt-1">{frontendValidation.message}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <XCircle className="w-5 h-5 text-danger-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-danger-900">Validation Issues Found</p>
                      <ul className="mt-2 space-y-1">
                        {frontendValidation.errors?.map((err, index) => (
                          <li key={index} className="text-xs text-danger-700">
                            <span className="font-mono font-medium">{err.field}</span>: {err.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Output */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h2 className="text-lg font-semibold text-gray-900">Output</h2>
                {(generatedMessage || signedMessage) && (
                  <div className="flex items-center space-x-2 border-b border-gray-200">
                    <button
                      onClick={() => setActiveTab('unsigned')}
                      className={`px-3 py-1 text-sm font-medium transition-colors ${
                        activeTab === 'unsigned'
                          ? 'text-primary-600 border-b-2 border-primary-600 -mb-px'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      disabled={!generatedMessage}
                    >
                      Unsigned
                    </button>
                    <button
                      onClick={() => setActiveTab('signed')}
                      className={`px-3 py-1 text-sm font-medium transition-colors ${
                        activeTab === 'signed'
                          ? 'text-primary-600 border-b-2 border-primary-600 -mb-px'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      disabled={!signedMessage}
                    >
                      Signed
                      {signedMessage && <Shield className="w-3 h-3 inline ml-1 text-success-600" />}
                    </button>
                  </div>
                )}
              </div>
              {((activeTab === 'unsigned' && generatedMessage) || (activeTab === 'signed' && signedMessage)) && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleCopy(activeTab === 'signed' ? signedMessage : generatedMessage)}
                    className={`btn-secondary text-sm flex items-center space-x-2 ${copied ? 'text-success-700' : ''}`}
                  >
                    {copied ? <Check className="w-4 h-4 text-success-600" /> : <Copy className="w-4 h-4" />}
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                  </button>
                  <button
                    onClick={() => handleDownload(
                      activeTab === 'signed' ? signedMessage : generatedMessage,
                      activeTab === 'signed'
                    )}
                    className="btn-secondary text-sm flex items-center space-x-2"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="card-body p-0">
            <pre className="w-full h-96 p-4 font-mono text-sm overflow-auto bg-gray-50">
              {activeTab === 'signed' && signedMessage
                ? signedMessage
                : activeTab === 'unsigned' && generatedMessage
                ? generatedMessage
                : 'Generated message will appear here...'}
            </pre>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          {error && (
            <div className="flex items-center space-x-2 text-danger-600">
              <XCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          {validationResult && validationResult.valid && (
            <div className="flex items-center space-x-2 text-success-600">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm">Backend validation passed</span>
            </div>
          )}
          {validationResult && !validationResult.valid && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-danger-600">
                <XCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Backend validation errors:</span>
              </div>
              <ul className="list-disc list-inside space-y-1 text-sm text-danger-600">
                {validationResult.errors?.map((err, index) => (
                  <li key={index}>
                    {err.field}: {err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <label className="flex items-center gap-2 text-sm text-gray-600 mr-auto sm:mr-0">
            <input type="checkbox" checked={keepIds} onChange={e => setKeepIds(e.target.checked)}
              className="rounded border-gray-300" />
            Keep these ids (to test a replay)
          </label>
          <button
            onClick={generateMessage}
            disabled={loading}
            className="btn-secondary flex items-center space-x-2"
          >
            {loading && !signing ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Generate Only</span>
              </>
            )}
          </button>
          <button
            onClick={generateAndSign}
            disabled={loading}
            className="btn-primary flex items-center space-x-2"
          >
            {signing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Generating & Signing...</span>
              </>
            ) : (
              <>
                <Shield className="w-4 h-4" />
                <span>Generate & Sign</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Sample Information Panel */}
      {currentSample && (
        <div className="card bg-blue-50 border-blue-200">
          <div className="card-body">
            <div className="flex items-start space-x-3">
              <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  {currentSample.name}
                </h3>
                <p className="text-sm text-blue-800 mb-3">{currentSample.description}</p>
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-blue-900">Key Features:</p>
                  <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                    <li>Click <span className="font-semibold">Sample</span> button to load pre-configured JSON</li>
                    <li>Click <span className="font-semibold">Validate</span> for instant structure checking</li>
                    <li><span className="font-semibold">Generate Only</span> creates unsigned {outputFormat.toUpperCase()} message</li>
                    <li><span className="font-semibold">Generate & Sign</span> also signs it here, in the Sandbox; the messages your gateway sends must be signed by your gateway</li>
                    <li>Each Generate stamps new message, transaction and end-to-end ids, unless you tick “Keep these ids”</li>
                    <li>All fields use camelCase naming (e.g., "grpHdr", "msgId")</li>
                    <li>Date/time fields must be in ISO 8601 format</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Tips */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-gray-600" />
            <h3 className="text-sm font-semibold text-gray-900">Validation Layers</h3>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-900">Frontend Validation (Instant)</h4>
              <ul className="text-gray-700 space-y-1 list-disc list-inside">
                <li>JSON syntax checking</li>
                <li>Required field presence</li>
                <li>Data structure validation</li>
                <li>Date/time format checking</li>
                <li>Currency code validation</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-900">Backend Validation (API)</h4>
              <ul className="text-gray-700 space-y-1 list-disc list-inside">
                <li>XSD schema compliance</li>
                <li>Business rule validation</li>
                <li>Cross-field dependencies</li>
                <li>Regulatory requirements</li>
                <li>Switch-specific rules</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageGenerator;
